import type { ConsoleBrandDetail, ProvenanceSource } from '@rsecure/brand-contracts/console';
import { Badge, Ltr, Notice, SourceTag, Time } from '../../components/ui';
import { show } from '../../lib/diff';

const LEGEND: Record<ProvenanceSource, string> = {
  curated: 'Human-curated input (candidate / legacy inventory row)',
  brandfetch_live: 'Brandfetch API response (domain fetch or webhook) — a provider claim',
  brandfetch_archive: 'Historical Brandfetch payload restored from the archive — a provider claim',
  measured: 'Computed by V3 from the actual bytes',
  policy: 'Decided by an automated V3 policy',
  human_review: 'Decided by a recorded human review',
  derived: 'Derived by V3 from other fields',
};

export function ProvenanceTab({ d }: { d: ConsoleBrandDetail }) {
  if (!d.provenance.length)
    return (
      <Notice tone="warn">
        No normalized revision exists, so there is no field-level provenance. The only persisted
        inputs are the candidate rows and snapshots listed on Overview, and the review evidence on
        Reviews.
      </Notice>
    );
  const counts = d.provenance.reduce<Record<string, number>>((m, p) => {
    m[p.source] = (m[p.source] ?? 0) + 1;
    return m;
  }, {});
  const flagged = d.provenance.filter((p) => p.flag);
  return (
    <div className="grid" style={{ gap: 16 }}>
      <Notice>
        Provider values are <strong>claims</strong>. Identity is a separate, recorded decision
        (policy or human) — see the “identity” row. Revision #{d.contentRevision?.number} ·{' '}
        {flagged.length} field(s) missing or conflicting.
      </Notice>
      <section className="swatches" aria-label="Legend">
        {(Object.keys(LEGEND) as ProvenanceSource[]).map((s) => (
          <span key={s} className="sw" title={LEGEND[s]}>
            <SourceTag source={s} />
            <span className="muted" style={{ fontSize: 12 }}>
              {counts[s] ?? 0}
            </span>
          </span>
        ))}
      </section>
      <section className="card">
        <div className="table-wrap">
          <table className="t" data-testid="provenance-table">
            <thead>
              <tr>
                <th scope="col">Field</th>
                <th scope="col">Value</th>
                <th scope="col">Source</th>
                <th scope="col">Evidence</th>
                <th scope="col">At</th>
              </tr>
            </thead>
            <tbody>
              {d.provenance.map((p) => (
                <tr key={p.field}>
                  <td className="mono nowrap">
                    {p.field}
                    {p.flag && (
                      <>
                        {' '}
                        <Badge tone={p.flag === 'conflict' ? 'bad' : 'warn'}>{p.flag}</Badge>
                      </>
                    )}
                  </td>
                  <td style={{ maxWidth: 360 }}>
                    {show(p.value) ? (
                      <span style={{ overflowWrap: 'anywhere' }}>
                        {String(show(p.value)).length > 220
                          ? `${String(show(p.value)).slice(0, 220)}…`
                          : show(p.value)}
                      </span>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                  <td>
                    <SourceTag source={p.source} />
                  </td>
                  <td className="muted" style={{ fontSize: 12.5, maxWidth: 420 }}>
                    {p.detail}
                  </td>
                  <td>
                    <Time iso={p.at} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="muted" style={{ fontSize: 12.5 }}>
        Snapshot of record:{' '}
        <Ltr mono>
          {(d.content?.provider as { brandfetchId?: string } | undefined)?.brandfetchId ?? '—'}
        </Ltr>{' '}
        · raw payloads live in the private bucket and are never served by the console.
      </p>
    </div>
  );
}
